/**
 * Redux slice for AI & Speech Services configuration.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import aiServicesConfigService, {
  AIConfigResponse,
  STTConfig,
  LLMProviderConfig,
  RoutingEntry,
  CopilotConfig,
  LLMConfig,
  ProviderHealthStatus,
  RoutingUpdateRequest,
} from '../services/aiServicesConfigService';

// ── State ──────────────────────────────────────────────────────────────

export interface AIServicesState {
  stt: STTConfig;
  llmProviders: LLMProviderConfig[];
  routing: RoutingEntry[];
  copilot: CopilotConfig;
  providerHealth: ProviderHealthStatus[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  lastLoaded: string | null;
}

const initialState: AIServicesState = {
  stt: {
    primary_provider: 'assemblyai',
    fallback_provider: 'deepgram',
    streaming_mode: 'realtime',
    language: 'en',
    pause_detection_ms: 700,
  },
  llmProviders: [],
  routing: [],
  copilot: {
    enabled: true,
    retrieval_mode: 'hybrid',
    max_context_size: 4000,
    confidence_threshold: 0.3,
    error: null,
  },
  providerHealth: [],
  loading: false,
  saving: false,
  error: null,
  lastLoaded: null,
};

// ── Async Thunks ───────────────────────────────────────────────────────

export const fetchAIConfig = createAsyncThunk(
  'aiServices/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      return await aiServicesConfigService.getConfig();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      return rejectWithValue(
        e.response?.data?.detail || e.message || 'Failed to load AI config'
      );
    }
  }
);

export const saveSTTConfig = createAsyncThunk(
  'aiServices/saveSTT',
  async (stt: Partial<STTConfig>, { rejectWithValue }) => {
    try {
      await aiServicesConfigService.updateConfig({ stt });
      return stt;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      return rejectWithValue(
        e.response?.data?.detail || e.message || 'Failed to save STT config'
      );
    }
  }
);

export const saveCopilotConfig = createAsyncThunk(
  'aiServices/saveCopilot',
  async (copilot: Partial<CopilotConfig>, { rejectWithValue }) => {
    try {
      await aiServicesConfigService.updateConfig({ copilot });
      return copilot;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      return rejectWithValue(
        e.response?.data?.detail || e.message || 'Failed to save Copilot config'
      );
    }
  }
);

export const saveLLMConfig = createAsyncThunk(
  'aiServices/saveLLM',
  async (llm: Partial<LLMConfig>, { rejectWithValue }) => {
    try {
      await aiServicesConfigService.updateConfig({ llm });
      return llm;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      return rejectWithValue(
        e.response?.data?.detail || e.message || 'Failed to save LLM config'
      );
    }
  }
);

export const updateRoutingEntry = createAsyncThunk(
  'aiServices/updateRouting',
  async (req: RoutingUpdateRequest, { rejectWithValue }) => {
    try {
      const result = await aiServicesConfigService.updateRouting(req);
      return { service: req.service, result };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      return rejectWithValue(
        e.response?.data?.detail || e.message || 'Failed to update routing'
      );
    }
  }
);

export const refreshProviderHealth = createAsyncThunk(
  'aiServices/refreshHealth',
  async (_, { rejectWithValue }) => {
    try {
      return await aiServicesConfigService.getProviderHealth();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      return rejectWithValue(
        e.response?.data?.detail || e.message || 'Failed to check provider health'
      );
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────

const aiServicesSlice = createSlice({
  name: 'aiServices',
  initialState,
  reducers: {
    clearAIServicesError(state) {
      state.error = null;
    },
    updateSTTLocal(state, action: PayloadAction<Partial<STTConfig>>) {
      state.stt = { ...state.stt, ...action.payload };
    },
    updateCopilotLocal(state, action: PayloadAction<Partial<CopilotConfig>>) {
      state.copilot = { ...state.copilot, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // fetchAIConfig
    builder
      .addCase(fetchAIConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAIConfig.fulfilled, (state, action: PayloadAction<AIConfigResponse>) => {
        state.loading = false;
        state.stt = action.payload.stt;
        state.llmProviders = action.payload.llm_providers;
        state.routing = action.payload.routing;
        state.copilot = action.payload.copilot;
        state.providerHealth = action.payload.provider_health;
        state.lastLoaded = new Date().toISOString();
      })
      .addCase(fetchAIConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // saveSTTConfig
    builder
      .addCase(saveSTTConfig.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveSTTConfig.fulfilled, (state, action) => {
        state.saving = false;
        state.stt = { ...state.stt, ...action.payload };
      })
      .addCase(saveSTTConfig.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    // saveCopilotConfig
    builder
      .addCase(saveCopilotConfig.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveCopilotConfig.fulfilled, (state, action) => {
        state.saving = false;
        state.copilot = { ...state.copilot, ...action.payload };
      })
      .addCase(saveCopilotConfig.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    // saveLLMConfig
    builder
      .addCase(saveLLMConfig.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveLLMConfig.fulfilled, (state, action) => {
        state.saving = false;
        const primary = action.payload.primary_provider;
        if (primary !== undefined && primary !== null) {
          state.llmProviders = state.llmProviders.map((p) => ({
            ...p,
            enabled: primary === 'both' || p.provider_id === primary,
          }));
        }
      })
      .addCase(saveLLMConfig.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    // updateRoutingEntry
    builder
      .addCase(updateRoutingEntry.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateRoutingEntry.fulfilled, (state, action) => {
        state.saving = false;
        // Update the routing entry in-place
        const { service, result } = action.payload;
        const idx = state.routing.findIndex((r) => r.service === service);
        if (idx >= 0) {
          state.routing[idx] = {
            ...state.routing[idx],
            primary_provider: result.primary.provider,
            primary_model: result.primary.model,
            fallback_provider: result.fallback.provider,
            fallback_model: result.fallback.model,
          };
        }
      })
      .addCase(updateRoutingEntry.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    // refreshProviderHealth
    builder
      .addCase(refreshProviderHealth.fulfilled, (state, action) => {
        state.providerHealth = action.payload;
      });
  },
});

export const { clearAIServicesError, updateSTTLocal, updateCopilotLocal } = aiServicesSlice.actions;
export default aiServicesSlice.reducer;
